/**
 * Create Group Modal
 *
 * SlideoverModal to create a new group (matches app theme):
 *  - Enter group name
 *  - Multi-select members from the real user list
 *  - Click "Create Group" to finish
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Lucide from "src/base-components/Lucide";
import Button from "src/base-components/Button";
import SlideoverModal from "../../SlideoverModal";
import TestImage from "../../../assets/images/TestImages/user.png";
import { ChatParticipant } from "src/pubnub";
import { ChatUser } from "../index";
import { Form } from "react-router-dom";
import { FormInput } from "src/base-components/Form";

/* ---- Props ---- */

export interface GroupCreationData {
  name: string;
  members: ChatParticipant[];
}

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGroup: (data: GroupCreationData) => void;
  chatUsers: ChatUser[];
  currentUserId: string;
  currentUserName: string;
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSearch?: (query: string) => void;
}

/* ---- Component ---- */

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onCreateGroup,
  chatUsers,
  currentUserId,
  currentUserName,
  loading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
}) => {
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const userListRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const selectedIds = useMemo(() => new Set(selectedUsers.map((u) => u.id)), [selectedUsers]);

  // Build available users from props (exclude self AND already-selected)
  const availableUsers = useMemo(
    () =>
      chatUsers
        .filter((u) => String(u.id) !== currentUserId && !selectedIds.has(String(u.id)))
        .map((u) => ({
          id: String(u.id),
          name: `${u.name}${u.last_name ? " " + u.last_name : ""}`,
          email: u.email,
        })),
    [chatUsers, currentUserId, selectedIds]
  );

  // Infinite scroll for user list
  const handleUserListScroll = useCallback(() => {
    const el = userListRef.current;
    if (!el || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onLoadMore?.();
    }
  }, [loading, hasMore, onLoadMore]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGroupName("");
      setSelectedUsers([]);
      setSearchQuery("");
      onSearch?.("");
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [isOpen]);

  const addMember = (user: { id: string; name: string; email: string }) => {
    setSelectedUsers((prev) => [...prev, user]);
  };

  const removeMember = (id: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  };

  // Debounced search — calls parent API search after 400ms
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch?.(value), 400);
  };

  const handleCreate = () => {
    if (!groupName.trim() || selectedUsers.length === 0) return;

    const members: ChatParticipant[] = [
      { id: currentUserId, name: currentUserName },
      ...selectedUsers.map((u) => ({ id: u.id, name: u.name })),
    ];

    onCreateGroup({ name: groupName.trim(), members });
  };

  return (
    <SlideoverModal
      size="lg"
      visible={isOpen}
      onClose={onClose}
      headerLeft={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)] flex items-center justify-center">
            <Lucide icon="Users" className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-[16px] text-[#1D293D] font-bold">New Group</h2>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 space-y-5">
          {/* Group Name */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Group Name</label>
            <div className="relative">
              <Lucide
                icon="Users"
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Enter group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-0 focus:border-slate-200 bg-slate-50"
                autoFocus
              />
            </div>
          </div>

          {/* Selected Members Chips */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                >
                  <img src={TestImage} className="w-4 h-4 rounded-full" alt="" />
                  {u.name}
                  <button onClick={() => removeMember(u.id)} className="hover:text-red-500">
                    <Lucide icon="X" className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search Users */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
              Add Members ({selectedUsers.length} selected)
            </label>
            <div className="relative mb-2">
              <Lucide
                icon="Search"
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-0 focus:border-slate-200 bg-slate-50"
              />
            </div>

            {/* User List */}
            <div
              ref={userListRef}
              className="max-h-[calc(110vh-430px)] overflow-y-auto space-y-1 rounded-lg p-1"
              onScroll={handleUserListScroll}
            >
              {availableUsers.length === 0 && !loading ? (
                <p className="text-center text-sm text-slate-400 py-4">No users found</p>
              ) : (
                availableUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => addMember(user)}
                    className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition hover:bg-slate-50"
                  >
                    <img src={TestImage} className="w-8 h-8 rounded-full" alt="" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-slate-400">{user.email}</p>
                    </div>
                    <Lucide icon="Plus" className="w-4 h-4 text-purple-600" />
                  </div>
                ))
              )}
              {loading && (
                <div className="flex items-center justify-center py-3">
                  <Lucide icon="Loader" className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline-secondary"
            onClick={onClose}
            className="px-4 py-2 h-[38px] text-[12px]"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!groupName.trim() || selectedUsers.length === 0}
            className="px-6 py-2 h-[38px] text-[12px] bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)]"
          >
            <Lucide icon="Users" className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>
      </div>
    </SlideoverModal>
  );
};

export default CreateGroupModal;
